/**
 * Клиентская подготовка изображений (CC §8.3, CLIENT_WEB.md §10.2).
 *
 * Ключевое требование §10.2 п. 1: поворот по EXIF применяется к пикселям
 * через `createImageBitmap(file, {imageOrientation: 'from-image'})`, но
 * полагаться на это БЕЗ ПРОВЕРКИ запрещено — неизвестные поля словаря опций
 * браузер молча игнорирует, и фотография ушла бы на сервер необратимо
 * повёрнутой (EXIF сервер срезает). Поэтому ориентация читается из байтов
 * файла, и если браузер опцию проигнорировал, поворот выполняется вручную.
 */
import type { MediaLimit } from '@shared/api/types'

import {
  checkMegapixelsBeforeDecode,
  checkMinSide,
  checkSize,
  checkType,
  JPEG_QUALITY,
  MAX_DIMENSION_PX,
  type LocalRejection,
} from './limits'

export interface PreparedImage {
  blob: Blob
  /** Базовый MIME итогового файла — уходит в `POST /media/uploads`. */
  contentType: string
  width: number
  height: number
  /** Локальный URL для превью до отправки (C-11). */
  previewUrl: string
}

export type PrepareResult = { ok: true; image: PreparedImage } | { ok: false; rejection: LocalRejection }

/* ===================================================================== */
/* EXIF                                                                   */
/* ===================================================================== */

interface JpegHeaderInfo {
  /** 1..8 по EXIF; 1 — поворот не требуется. */
  orientation: number
  /** Размеры пикселей ДО применения ориентации (маркер SOF). */
  rawWidth: number
  rawHeight: number
}

/** Ориентации, при которых стороны меняются местами. */
function swapsDimensions(orientation: number): boolean {
  return orientation >= 5 && orientation <= 8
}

/**
 * Разбирает заголовок JPEG: ориентацию из APP1/EXIF и размеры из SOF.
 * Читается только начало файла — полный декод для этого не нужен.
 */
async function readJpegHeader(file: File): Promise<JpegHeaderInfo | null> {
  // 256 КБ с запасом перекрывают EXIF-блок и первый SOF у любой камеры.
  const head = new DataView(await file.slice(0, 256 * 1024).arrayBuffer())
  if (head.byteLength < 4) return null
  if (head.getUint16(0, false) !== 0xffd8) return null // не JPEG

  let orientation = 1
  let rawWidth = 0
  let rawHeight = 0
  let offset = 2

  while (offset + 4 <= head.byteLength) {
    if (head.getUint8(offset) !== 0xff) {
      offset += 1
      continue
    }

    const marker = head.getUint8(offset + 1)
    const length = head.getUint16(offset + 2, false)
    if (length < 2) break

    // SOF0..SOF15, кроме DHT (c4), JPG (c8) и DAC (cc) — в них размеры кадра.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      if (offset + 9 <= head.byteLength) {
        rawHeight = head.getUint16(offset + 5, false)
        rawWidth = head.getUint16(offset + 7, false)
      }
      if (orientation !== 1 || rawWidth) break
    }

    if (marker === 0xe1) {
      const parsed = readExifOrientation(head, offset + 4, length - 2)
      if (parsed !== null) orientation = parsed
    }

    offset += 2 + length
  }

  if (!rawWidth || !rawHeight) return null
  return { orientation, rawWidth, rawHeight }
}

/** Читает тег Orientation (0x0112) из блока APP1. */
function readExifOrientation(view: DataView, start: number, length: number): number | null {
  if (start + 6 > view.byteLength) return null

  // Сигнатура "Exif\0\0"
  if (view.getUint32(start, false) !== 0x45786966) return null

  const tiff = start + 6
  if (tiff + 8 > view.byteLength) return null

  const littleEndian = view.getUint16(tiff, false) === 0x4949
  const firstIfd = view.getUint32(tiff + 4, littleEndian)
  const ifd = tiff + firstIfd
  if (ifd + 2 > view.byteLength) return null

  const count = view.getUint16(ifd, littleEndian)

  for (let i = 0; i < count; i += 1) {
    const entry = ifd + 2 + i * 12
    if (entry + 12 > view.byteLength || entry > start + length) break

    if (view.getUint16(entry, littleEndian) === 0x0112) {
      const value = view.getUint16(entry + 8, littleEndian)
      return value >= 1 && value <= 8 ? value : 1
    }
  }

  return null
}

/** Матрица преобразования холста для каждой EXIF-ориентации. */
function applyOrientation(
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  orientation: number,
  width: number,
  height: number,
): void {
  switch (orientation) {
    case 2:
      context.transform(-1, 0, 0, 1, width, 0)
      break
    case 3:
      context.transform(-1, 0, 0, -1, width, height)
      break
    case 4:
      context.transform(1, 0, 0, -1, 0, height)
      break
    case 5:
      context.transform(0, 1, 1, 0, 0, 0)
      break
    case 6:
      context.transform(0, 1, -1, 0, width, 0)
      break
    case 7:
      context.transform(0, -1, -1, 0, width, height)
      break
    case 8:
      context.transform(0, -1, 1, 0, 0, height)
      break
    default:
      break
  }
}

/* ===================================================================== */
/* Подготовка                                                             */
/* ===================================================================== */

export async function prepareImage(
  file: File,
  limit: MediaLimit,
  // Аватар канала обрабатывается как фото профиля: квадратный кадр, те же лимиты (v1.7).
  purpose: 'profile_photo' | 'chat_image' | 'channel_photo',
): Promise<PrepareResult> {
  const typeRejection = checkType(limit, file)
  if (typeRejection) return { ok: false, rejection: typeRejection }

  const header = await readJpegHeader(file)

  // §10.2 п. 1a: отказ ДО декодирования, если мегапикселей заведомо много.
  if (header) {
    const tooBig = checkMegapixelsBeforeDecode(limit, header.rawWidth, header.rawHeight)
    if (tooBig) return { ok: false, rejection: tooBig }
  }

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    /*
     * §10.2 п. 5: HEIC не конвертируется. Chromium и Firefox его не декодируют —
     * если тип разрешён лимитом и размер в пределах, файл уходит КАК ЕСТЬ.
     */
    const sizeRejection = checkSize(limit, file.size)
    if (sizeRejection) return { ok: false, rejection: sizeRejection }

    return {
      ok: true,
      image: {
        blob: file,
        contentType: file.type,
        width: 0,
        height: 0,
        previewUrl: URL.createObjectURL(file),
      },
    }
  }

  try {
    /*
     * ПРОВЕРКА §10.2 п. 1. Если EXIF говорит о повороте на 90°/270°, у корректно
     * применённой ориентации стороны bitmap'а поменяны местами относительно SOF.
     * Совпадение означает, что браузер опцию проигнорировал, — доворачиваем сами.
     */
    const exifOrientation = header?.orientation ?? 1
    const needsManualRotation =
      header !== null &&
      exifOrientation !== 1 &&
      (swapsDimensions(exifOrientation)
        ? bitmap.width === header.rawWidth && bitmap.height === header.rawHeight
        : false)

    const sourceWidth = bitmap.width
    const sourceHeight = bitmap.height

    // Размеры после возможного ручного доворота.
    const orientedWidth = needsManualRotation && swapsDimensions(exifOrientation) ? sourceHeight : sourceWidth
    const orientedHeight =
      needsManualRotation && swapsDimensions(exifOrientation) ? sourceWidth : sourceHeight

    const megapixelRejection = checkMegapixelsBeforeDecode(limit, orientedWidth, orientedHeight)
    if (megapixelRejection) return { ok: false, rejection: megapixelRejection }

    // §10.2 п. 2: масштабирование до 2048 px по большей стороне.
    const maxSide = Math.max(orientedWidth, orientedHeight)
    let scale = maxSide > MAX_DIMENSION_PX ? MAX_DIMENSION_PX / maxSide : 1

    // Ограничение по мегапикселям лимита, если оно строже.
    if (limit.max_megapixels) {
      const pixels = orientedWidth * orientedHeight * scale * scale
      const capped = limit.max_megapixels * 1_000_000
      if (pixels > capped) scale *= Math.sqrt(capped / pixels)
    }

    let targetWidth = Math.max(1, Math.round(orientedWidth * scale))
    let targetHeight = Math.max(1, Math.round(orientedHeight * scale))

    /*
     * §10.2 п. 4: для фото профиля меньшая сторона результата ОБЯЗАНА остаться
     * не ниже `min_side_px`. Если исходник меньше — уменьшение не выполняется;
     * если и после этого сторона ниже порога, файл отклоняется локально.
     */
    if (purpose === 'profile_photo' && limit.min_side_px) {
      if (Math.min(orientedWidth, orientedHeight) < limit.min_side_px) {
        return { ok: false, rejection: checkMinSide(limit, orientedWidth, orientedHeight)! }
      }
      if (Math.min(targetWidth, targetHeight) < limit.min_side_px) {
        targetWidth = orientedWidth
        targetHeight = orientedHeight
        scale = 1
      }
    }

    // Уменьшать нечего и поворачивать нечего — отправляем исходник как есть.
    if (scale === 1 && !needsManualRotation) {
      const sizeRejection = checkSize(limit, file.size)
      if (sizeRejection) return { ok: false, rejection: sizeRejection }

      return {
        ok: true,
        image: {
          blob: file,
          contentType: file.type,
          width: orientedWidth,
          height: orientedHeight,
          previewUrl: URL.createObjectURL(file),
        },
      }
    }

    const blob = await renderToJpeg(bitmap, {
      width: targetWidth,
      height: targetHeight,
      orientation: needsManualRotation ? exifOrientation : 1,
    })

    // §10.2 п. 7: `size_bytes` — размер ИТОГОВОГО blob'а, иначе 409 size_mismatch.
    const sizeRejection = checkSize(limit, blob.size)
    if (sizeRejection) return { ok: false, rejection: sizeRejection }

    return {
      ok: true,
      image: {
        blob,
        contentType: 'image/jpeg',
        width: targetWidth,
        height: targetHeight,
        previewUrl: URL.createObjectURL(blob),
      },
    }
  } finally {
    bitmap.close()
  }
}

/**
 * §10.2 п. 3: экспорт через `OffscreenCanvas.convertToBlob`, а при его
 * недоступности — `HTMLCanvasElement.toBlob` (у элемента нет `convertToBlob`).
 */
async function renderToJpeg(
  bitmap: ImageBitmap,
  target: { width: number; height: number; orientation: number },
): Promise<Blob> {
  const { width, height, orientation } = target

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext('2d')
    if (context) {
      drawOriented(context, bitmap, width, height, orientation)
      return canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY })
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) throw new Error('canvas 2d context is not available')

  drawOriented(context, bitmap, width, height, orientation)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      JPEG_QUALITY,
    )
  })
}

function drawOriented(
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  bitmap: ImageBitmap,
  width: number,
  height: number,
  orientation: number,
): void {
  context.save()

  if (orientation !== 1) {
    applyOrientation(context, orientation, width, height)
    // После поворота на 90°/270° рисуем в перевёрнутой системе координат.
    if (swapsDimensions(orientation)) context.drawImage(bitmap, 0, 0, height, width)
    else context.drawImage(bitmap, 0, 0, width, height)
  } else {
    context.drawImage(bitmap, 0, 0, width, height)
  }

  context.restore()
}

/* ===================================================================== */
/* Видео и голосовые: длительность без перекодирования                    */
/* ===================================================================== */

/** §10.2 п. 6: видео и голосовые не перекодируются — нужна только длительность. */
export function readMediaDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const isVideo = file.type.startsWith('video')
    const element = document.createElement(isVideo ? 'video' : 'audio')
    const url = URL.createObjectURL(file)

    const cleanup = (): void => {
      URL.revokeObjectURL(url)
      element.removeAttribute('src')
    }

    element.preload = 'metadata'
    element.onloadedmetadata = () => {
      const duration = Number.isFinite(element.duration) ? Math.round(element.duration * 1000) : null
      cleanup()
      resolve(duration)
    }
    element.onerror = () => {
      cleanup()
      resolve(null)
    }

    element.src = url
  })
}
